import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, IconButton, LinearProgress, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import emailFill from '@iconify/icons-eva/email-fill';
import linkedinFill from '@iconify/icons-eva/linkedin-fill';
import plusFill from '@iconify/icons-eva/plus-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import alertCircleFill from '@iconify/icons-eva/alert-circle-fill';
import Page from '../Page';
import PageHeader from './shared/PageHeader';
import StatusChip from './shared/StatusChip';
import {
  listOutlookAccounts, listUnipileAccounts, connectUnipileAccount, disconnectUnipileAccount,
  checkUnipileAccountHealth, createUnipileHostedLink, syncUnipileAccounts,
} from '../../utils/outreachApi';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const OutlookCard = ({ account }) => {
  const expired = account.health === 'token_expired';
  const userName = account.user?.name || 'Unknown User';
  return (
    <Card variant="outlined" sx={{ borderColor: expired ? 'error.light' : 'divider' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: 'primary.lighter',
                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon icon={emailFill} width={20} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>{userName}</Typography>
            <Typography variant="caption" color="text.secondary">{account.email}</Typography>
          </Box>
          <StatusChip status={account.health} />
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Used in campaigns</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{account.used_in_campaigns}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Sent today</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{account.sent_today}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Token expires</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(account.token_expires_at)}</Typography>
          </Box>
        </Stack>
        {expired ? (
          <Stack direction="row" spacing={1} sx={{ p: 1, bgcolor: 'error.lighter', borderRadius: 1 }}
                 alignItems="center">
            <Icon icon={alertCircleFill} color="#B72136" />
            <Typography variant="body2" sx={{ flex: 1 }}>OAuth token expired — reconnect required</Typography>
            <Button size="small" variant="contained" color="error" startIcon={<Icon icon={refreshFill} />}>
              Reconnect
            </Button>
          </Stack>
        ) : null}
      </CardContent>
    </Card>
  );
};

const UnipileCard = ({ account, onDisconnect, onCheckHealth }) => {
  const [checking, setChecking] = React.useState(false);
  const capUsed = Math.min(100, (account.actions_today / account.daily_action_cap) * 100);
  const capColor = capUsed > 90 ? 'error.main' : capUsed > 60 ? 'warning.main' : 'success.main';
  const displayName = account.profile_name || account.display_name || 'Unnamed account';

  const handleCheckHealth = async () => {
    setChecking(true);
    try {
      await onCheckHealth(account.id);
    } finally {
      setChecking(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: '#E8F1FA',
                     display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0A66C2' }}>
            <Icon icon={linkedinFill} width={20} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>{displayName}</Typography>
            <Typography variant="caption" color="text.secondary">in/{account.linkedin_handle}</Typography>
          </Box>
          <StatusChip status={account.health} />
          <Tooltip title="Refresh health status">
            <IconButton size="small" onClick={handleCheckHealth} disabled={checking}
                        sx={{ color: 'text.secondary' }}>
              <Icon icon={refreshFill} width={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Disconnect">
            <IconButton size="small" onClick={() => onDisconnect(account.id)} color="error">
              <Icon icon={trash2Fill} width={18} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Box sx={{ mb: 1 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Daily action cap</Typography>
            <Typography variant="caption" sx={{ color: capColor, fontWeight: 600 }}>
              {account.actions_today}/{account.daily_action_cap}
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={capUsed}
                          sx={{ height: 6, borderRadius: 1,
                                '& .MuiLinearProgress-bar': { bgcolor: capColor } }} />
        </Box>

        <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Connected</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(account.connected_at).split(',')[0]}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Last sync</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(account.last_sync_at)}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ConnectedAccounts = () => {
  const [outlook, setOutlook] = useState(null);
  const [unipile, setUnipile] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ account_id: '', handle: '' });
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [hostedStarting, setHostedStarting] = useState(false);
  const [hostedPolling, setHostedPolling] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const onSyncFromUnipile = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await syncUnipileAccounts();
      setSyncResult({
        ok: true,
        text: `Imported ${data.created} new, refreshed ${data.updated} existing.`,
      });
      load();
    } catch (err) {
      setSyncResult({
        ok: false,
        text: err?.response?.data?.detail || 'Sync failed.',
      });
    } finally {
      setSyncing(false);
    }
  };

  const load = () => {
    Promise.allSettled([listOutlookAccounts(), listUnipileAccounts()]).then(([o, u]) => {
      if (o.status === 'fulfilled') setOutlook(o.value.data || []);
      if (u.status === 'fulfilled') setUnipile(u.value.data || []);
    });
  };
  useEffect(() => { load(); }, []);

  // ── Unipile Hosted Auth flow ────────────────────────────────────────────────
  // 1. POST to backend → backend asks Unipile for a one-shot login URL.
  // 2. Open the URL in a new tab — user logs in on Unipile's hosted page.
  // 3. Unipile's notify_url webhook hits our backend on success → a row appears
  //    in UnipileAccount. We poll the accounts list for ~60 seconds to surface it
  //    without the admin needing to refresh.
  const onConnectViaHosted = async () => {
    setHostedStarting(true);
    try {
      const { data } = await createUnipileHostedLink({ providers: ['LINKEDIN'] });
      if (!data?.url) throw new Error('Backend did not return a hosted URL');
      window.open(data.url, '_blank', 'noopener,noreferrer');
      setHostedPolling(true);
    } catch (err) {
      setConnectError(err?.response?.data?.detail || 'Could not start hosted auth.');
    } finally {
      setHostedStarting(false);
    }
  };

  // Poll for new accounts after the hosted-auth flow opens. Stop when the
  // accounts list grows (= callback fired) or after 60 s.
  useEffect(() => {
    if (!hostedPolling) return undefined;
    const startedAt = Date.now();
    const startCount = (unipile || []).length;
    const id = setInterval(() => {
      listUnipileAccounts().then((res) => {
        const next = res.data || [];
        if (next.length > startCount) {
          setUnipile(next);
          setHostedPolling(false);
        } else if (Date.now() - startedAt > 60_000) {
          setHostedPolling(false);
        }
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostedPolling]);

  const onConnectUnipile = async () => {
    setConnecting(true);
    setConnectError('');
    try {
      await connectUnipileAccount({
        unipile_account_id: form.account_id.trim(),
        linkedin_handle:    form.handle.trim(),
      });
      setDialog(false);
      setForm({ account_id: '', handle: '' });
      load();
    } catch (err) {
      setConnectError(err?.response?.data?.detail || 'Connection failed — check the account ID.');
    }
    setConnecting(false);
  };

  const onDisconnect = async (id) => {
    await disconnectUnipileAccount(id);
    load();
  };

  const onCheckHealth = async (id) => {
    const res = await checkUnipileAccountHealth(id);
    setUnipile((prev) => prev.map((a) => (a.id === id ? { ...a, ...res.data } : a)));
  };

  return (
    <Page title="Outreach Accounts | CRM">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <PageHeader
          title="Outreach Accounts"
          subtitle="Outlook mailboxes (per BA) and Unipile/LinkedIn pool (admin-managed)"
        />

        <Card sx={{ p: 2.5, mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Outlook (BA mailboxes)</Typography>
              <Typography variant="body2" color="text.secondary">
                Sends originate from each BA's Outlook. Replies route back to their inbox.
              </Typography>
            </Box>
            <Chip label={`${outlook?.length || 0} connected`} variant="outlined" />
          </Stack>
          {!outlook ? <LinearProgress /> : (
            <Grid container spacing={2}>
              {outlook.map((a) => (
                <Grid item xs={12} md={6} lg={4} key={a.id}><OutlookCard account={a} /></Grid>
              ))}
            </Grid>
          )}
        </Card>

        <Card sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>LinkedIn (Unipile)</Typography>
              <Typography variant="body2" color="text.secondary">
                Centralized account pool — 100 actions/account/day per Unipile contract.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={`${unipile?.length || 0} connected`} variant="outlined" />
              {hostedPolling && (
                <Chip
                  label="Waiting for hosted login…"
                  size="small"
                  color="info"
                  variant="outlined"
                  icon={<CircularProgress size={10} sx={{ ml: 0.75 }} />}
                />
              )}
              <Button
                size="small" variant="contained"
                startIcon={hostedStarting ? <CircularProgress size={14} color="inherit" /> : <Icon icon={plusFill} />}
                disabled={hostedStarting}
                onClick={onConnectViaHosted}
              >
                Connect LinkedIn
              </Button>
              <Tooltip title="Pull every account from Unipile and import any we don't have. Use when a hosted-auth callback was missed.">
                <span>
                  <Button
                    size="small" variant="outlined"
                    startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <Icon icon={refreshFill} />}
                    disabled={syncing}
                    onClick={onSyncFromUnipile}
                  >
                    Sync from Unipile
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small" variant="text"
                onClick={() => setDialog(true)}
                sx={{ textTransform: 'none' }}
              >
                Paste account ID manually
              </Button>
            </Stack>
          </Stack>
          {syncResult && (
            <Box sx={{ mt: 1, mb: 1.5, px: 1.5, py: 1, borderRadius: 1,
                       bgcolor: syncResult.ok ? 'success.lighter' : 'error.lighter' }}>
              <Typography variant="caption" sx={{ fontWeight: 600,
                color: syncResult.ok ? 'success.dark' : 'error.dark' }}>
                {syncResult.text}
              </Typography>
            </Box>
          )}
          {!unipile ? <LinearProgress /> : unipile.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No LinkedIn accounts connected yet.</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {unipile.map((a) => (
                <Grid item xs={12} md={6} lg={4} key={a.id}>
                  <UnipileCard account={a} onDisconnect={onDisconnect} onCheckHealth={onCheckHealth} />
                </Grid>
              ))}
            </Grid>
          )}
        </Card>

        <Dialog open={dialog} onClose={() => { setDialog(false); setConnectError(''); }}
                maxWidth="xs" fullWidth>
          <DialogTitle>Connect LinkedIn account (Unipile)</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Unipile Account ID"
                fullWidth size="small" required
                value={form.account_id}
                onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                helperText="Found in Unipile dashboard → Accounts → Account ID"
              />
              <TextField
                label="LinkedIn profile URL"
                fullWidth size="small" required
                placeholder="https://www.linkedin.com/in/your-handle/"
                value={form.handle}
                onChange={(e) => setForm({ ...form, handle: e.target.value })}
                helperText="Full LinkedIn profile URL of the account"
              />
              {connectError && (
                <Typography variant="caption" color="error">{connectError}</Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDialog(false); setConnectError(''); }}>Cancel</Button>
            <Button variant="contained"
                    disabled={!form.account_id.trim() || !form.handle.trim() || connecting}
                    onClick={onConnectUnipile}>
              {connecting ? 'Connecting…' : 'Connect'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default ConnectedAccounts;
